<template>
  <Loader :value="promise" theme="overlay" class="leaflet">
    <div class="leaflet__aspect" :style="style">
      <div ref="map" class="leaflet__map content"></div>

      <div v-if="actions.length" class="leaflet__actions leaflet__actions--top-right">
        <ButtonComponent
          v-for="(item, i) in actions"
          :key="i"
          :label="item.label"
          :icon="item.icon"
          icon-placement="right"
          :aria-label="item.label || item.tooltip"
          v-tooltip="item.tooltip"
          @click="item.action"
        />
      </div>
    </div>
  </Loader>
</template>

<script>
import { mergeDeepRight, path } from 'ramda'
import Leaflet from 'leaflet'
import 'leaflet-draw'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import Localization from './localization'
import { getDimensions, setByPath } from '@/utils'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import { ButtonComponent } from 'vue-elder-button'

const DefaultStyle = {
  weight: 2,
  color: '#eeeeee',
  fillColor: '#999999',
  fillOpacity: 0.5,
  opacity: 1,
}

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    image: String,
    getStyle: {
      type: Function,
      default: () => ({}),
    },
    getTitle: {
      type: Function,
      default: () => undefined,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    controllers: {
      type: Array,
      default: () => ['rectangle', 'polygon'],
    },
    actions: {
      type: Array,
      default: () => [],
    },
    geometryPath: String,
    idResolver: [String, Function],
  },
  watch: {
    image: {
      handler: 'updateImage',
      immediate: true,
    },
    value: {
      handler() {
        if (this.image !== this.displayedImage) return
        this.render()
      },
    },
  },
  data() {
    return {
      promise: null,
      dimensions: null,
    }
  },
  computed: {
    ratio() {
      if (!this.dimensions) return 0
      return this.dimensions.width / this.dimensions.height
    },
    style() {
      return {
        '--map-ratio': this.ratio,
      }
    },
  },
  methods: {
    destroyMap() {
      if (!this.map) return
      this.map.off()
      this.map.remove()
      this.overlay = null
    },
    updateImage() {
      if (!this.map || !this.overlay) return this.init()

      const url = this.image

      const image = new Image()
      image.src = url

      return image
        .decode()
        .then(() => {
          if (url !== this.image) return

          const same =
            this.dimensions &&
            image.naturalWidth === this.dimensions.width &&
            image.naturalHeight === this.dimensions.height
          if (!same) return this.init()

          this.overlay.once('load', () => this.render())
          this.displayedImage = url
          this.overlay.setUrl(url)
        })
        .catch(() => {
          if (url !== this.image) return
          if (!this.overlay) return
          this.displayedImage = url
          this.overlay.setUrl(url)
          this.render()
        })
    },
    getGeometry(item) {
      if (!this.geometryPath) return item
      return path(this.geometryPath.split('.'), item)
    },
    getId(item) {
      if (!this.idResolver) return item
      if (typeof this.idResolver === 'function') return this.idResolver(item)
      return path(this.idResolver.split('.'), item)
    },
    render() {
      this.layer.clearLayers()

      this.value.forEach(item => {
        let layer
        let style = mergeDeepRight(DefaultStyle, this.getStyle(item))

        switch (this.getGeometry(item).geometry.type) {
          case 'Point':
            layer = Leaflet.circle(this.getGeometry(item).geometry.coordinates, item.properties.radius, style)
            break
          default:
            layer = Leaflet.GeoJSON.geometryToLayer(this.getGeometry(item), style)
            break
        }

        layer._external_id = this.getId(item)
        layer.on('click', event => {
          if (this.mode) return
          layer.event = event
          this.$emit('click:layer', layer)
        })
        layer.on('keypress', event => {
          if (event.originalEvent.key !== 'Enter') return
          if (this.mode) return
          layer.event = event
          this.$emit('click:layer', layer)
        })

        this.layer.addLayer(layer)

        let title = this.getTitle(item)
        if (title) layer.bindTooltip(title, { direction: 'center' })
      })
    },
    addLayer(layer) {
      this.$emit('add', this.prepareLayer(layer))
    },
    prepareLayer(layer) {
      let geo = layer.toGeoJSON()

      switch (geo.geometry.type) {
        case 'Point':
          return {
            type: 'Feature',
            properties: {
              radius: layer.getRadius(),
            },
            geometry: {
              type: 'Point',
              coordinates: [layer.getLatLng().lat, layer.getLatLng().lng],
            },
          }
        default:
          return geo
      }
    },
    editLayers(layers) {
      let items = {}

      layers.eachLayer(layer => (items[layer._external_id] = this.prepareLayer(layer)))

      this.$emit(
        'input',
        this.value.map(v => {
          let id = this.getId(v)
          if (id in items) {
            if (this.geometryPath)
              Object.entries(items[id]).forEach(([key, property]) =>
                setByPath(`${this.geometryPath}.${key}`, property, v),
              )
            else return items[id]
          }
          return v
        }),
      )
    },
    deleteLayers(layers) {
      let ids = []

      layers.eachLayer(layer => ids.push(layer._external_id))

      this.$emit(
        'input',
        this.value.filter(v => !ids.includes(this.getId(v))),
      )
    },
    init() {
      this.destroyMap()

      this.promise = getDimensions(this.image)
        .then(dimensions => {
          this.dimensions = dimensions
          return new Promise(resolve => {
            this.$nextTick(() => resolve(dimensions))
          })
        })
        .then(dimensions => {
          this.Leaflet = Leaflet

          Leaflet.CRS.CustomZoom = Leaflet.extend({}, Leaflet.CRS.Simple)

          Leaflet.Control.Zoom.mergeOptions({
            zoomInTitle: this.$t('zoomIn'),
            zoomOutTitle: this.$t('zoomOut'),
          })

          this.map = Leaflet.map(this.$refs.map, {
            minZoom: -3,
            maxZoom: 3,
            center: [0, 0],
            zoom: 0,
            zoomSnap: 0.01,
            maxBoundsViscosity: 0.9,
            scrollWheelZoom: false,
            crs: Leaflet.CRS.CustomZoom,
            ...this.options,
          })

          let southWest = this.map.unproject([0, dimensions.height], 0)
          let northEast = this.map.unproject([dimensions.width, 0], 0)
          let bounds = new Leaflet.LatLngBounds(southWest, northEast)

          this.overlay = Leaflet.imageOverlay(this.image, bounds).addTo(this.map)
          this.map.fitBounds(bounds, { animate: false })
          this.map.setMaxBounds(bounds, { animate: false })

          this.layer = new Leaflet.featureGroup()
          this.map.addLayer(this.layer)

          Leaflet.drawLocal = Localization

          let drawControl = new Leaflet.Control.Draw({
            position: 'bottomleft',
            edit: { featureGroup: this.layer },
            draw: {
              polyline: this.controllers.includes('polyline'),
              circle: this.controllers.includes('circle'),
              circlemarker: this.controllers.includes('circlemarker'),
              polygon: this.controllers.includes('polygon') ? { allowIntersection: false } : false,
              rectangle: this.controllers.includes('rectangle') ? { showArea: false } : false,
              marker: this.controllers.includes('marker'),
            },
          })

          this.map.addControl(drawControl)
          this.map.on(Leaflet.Draw.Event.CREATED, e => this.addLayer(e.layer))
          this.map.on(Leaflet.Draw.Event.EDITED, e => this.editLayers(e.layers))
          this.map.on(Leaflet.Draw.Event.DELETED, e => this.deleteLayers(e.layers))

          this.map.on(Leaflet.Draw.Event.EDITSTART, () => (this.mode = 'edit'))
          this.map.on(Leaflet.Draw.Event.EDITSTOP, () => {
            if (this.mode !== 'edit') return
            this.mode = null
          })

          this.map.on(Leaflet.Draw.Event.DELETESTART, () => (this.mode = 'delete'))
          this.map.on(Leaflet.Draw.Event.DELETESTOP, () => {
            if (this.mode !== 'delete') return
            this.mode = null
          })

          this.render()
          this.displayedImage = this.image
        })
    },
  },
  created() {
    this.map = null
    this.layer = null
    this.overlay = null
    this.mode = null
    this.displayedImage = null
    this.Leaflet = null
  },
  beforeDestroy() {
    this.destroyMap()
  },
  components: {
    Loader,
    ButtonComponent,
  },
}
</script>

<style lang="scss">
.leaflet {
  &__aspect {
    position: relative;
    aspect-ratio: var(--map-ratio);
  }

  &__map {
    height: 100%;
  }

  &__actions {
    position: absolute;
    z-index: 1000;
    display: flex;
    gap: 6px;
    pointer-events: none;

    &--top-right {
      top: 10px;
      right: 10px;
    }

    .elder-button {
      pointer-events: auto;
      background-color: #fff;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.3);
    }
  }

  &-div-icon {
    border-radius: 50%;
    background-color: css-alpha('error', 0.3);
  }

  &-control-attribution {
    display: none;
  }
}

.leaflet-container {
  font-family: 'Lato', Arial;
}
</style>
